// types
import React, { useContext, useEffect, useRef, useState } from "react";

import RecipeApi from "@/api/Recipe";

import { useAuth } from "@/customHooks/useAuth";
import { useCart } from "@/customHooks/useCart";
import { isNonProductionRecipe } from "@/utils/isNonProductionRecipe";
import { withoutAuthToast } from "@/utils/withoutAuthToast";
import { useQueryClient } from "@tanstack/react-query";
import Image from "next/image";
import { toast } from "react-hot-toast";
import type { Recipe } from "~types/recipe";

import { AddToCartContext } from "@/components/blocks/global/recipes-results/RecipesResults";
import DefaultLink from "@/components/elements/link/DefaultLink";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  Typography,
  useTheme
} from "@mui/material";

import Headset from "~public/icons/Headset/Headset.svg";
import LikeIcon from "~public/icons/Like/Like.svg";
import LikedIcon from "~public/icons/Like/Liked.svg";
import Pan from "~public/icons/Pan/Pan.svg";
import Saved from "~public/icons/Saved/Saved.svg";
import ShoppingBag from "~public/icons/Shopping Bag/Shopping Bag.svg";

interface RecipePanProps {
  recipe: Recipe;
}

export const RecipePan = React.memo(
  ({ recipe }: RecipePanProps): JSX.Element => {
    const [isRotated, setIsRotated] = useState<boolean>(false);
    const { handleOpenDrawer, setDrawerData, handleOpenFolderDrawer } =
      useContext(AddToCartContext);
    const { title, user, user_saved_recipe } = recipe;
    const { isInCart } = useCart();
    const { avatar_thumbnail_55, first_name, last_name } = user;
    const theme = useTheme();
    const ref = useRef<HTMLDivElement>();
    const { session } = useAuth();
    const client = useQueryClient();
    const [isLiked, setIsLiked] = useState(recipe?.is_liked);
    const [isLikedLoading, setIsLikedLoading] = useState(false);

    const panColor = () => {
      if (recipe.convenience_level?.[0]) {
        return theme.palette.green.main;
      }
      if (recipe.convenience_level?.[1]) {
        return theme.palette.pink.main;
      }
      if (recipe.convenience_level?.[2]) {
        return theme.palette.orange.main;
      }

      return theme.palette.gray.main;
    };

    const favoriteColor = () => {
      if (user_saved_recipe) {
        return theme.palette.orange.main;
      } else return "#344054";
    };

    const handleSavedClick = async (e: any) => {
      e.stopPropagation();
      if (!session) {
        return withoutAuthToast();
      }

      setDrawerData({
        pk: recipe.pk,
        convenience_level: recipe.convenience_level
      });
      handleOpenFolderDrawer();
    };

    const handleLikeClick = async (event: any) => {
      event.stopPropagation();

      if (!session) {
        return withoutAuthToast();
      }

      setIsLikedLoading(true);
      RecipeApi.addOrRemoveLike({ recipeId: recipe.pk })
        .then(() => {
          setIsLiked(prevState => !prevState);
          toast.success(isLiked ? "Disliked" : "Liked");
        })
        .catch(() => {
          toast.error("Please try again later");
        })
        .finally(() => {
          setIsLikedLoading(false);
        });
    };

    const handleShoppingBagClick = (e: any) => {
      e.stopPropagation();
      if (!session) {
        return withoutAuthToast();
      }

      setDrawerData({
        pk: recipe.pk,
        convenience_level: recipe.convenience_level
      });
      handleOpenDrawer();
    };

    useEffect(() => {
      const element = ref?.current;
      if (element) {
        element.addEventListener("focus", () => {
          console.log("focused");
          setIsRotated(prev => !prev);
        });
        element.addEventListener("mouseenter", () => {
          setIsRotated(true);
        });
        element.addEventListener("mouseleave", () => {
          setIsRotated(false);
        });
      }
      return () => {
        if (element) {
          element.removeEventListener("mouseenter", () => setIsRotated(true));
          element.removeEventListener("mouseleave", () => setIsRotated(false));
          element.removeEventListener("focus", () => {
            setIsRotated(prev => !prev);
          });
        }
      };
    }, []);

    return (
      <Box
        ref={ref}
        id={`recipe-pan_recipeId-${recipe.pk}`}
        data-qa={"recipe-pan"}
        data-cy={"recipe-pan"}
        className="recipe_pan"
        sx={{
          position: "relative",
          width: 115,
          height: 115,
          overflow: "visible",
          "& .pan path": { fill: panColor() },
          "& .pan ": { transition: "all 600ms ease-in-out" },
          "& .info_block": {
            mt: isRotated ? -4 : 0
          },
          "& .hidden_block": {
            opacity: isRotated ? 100 : 0,
            pointerEvents: isRotated ? "auto" : "none"
          },
          "& .avatar": { opacity: isRotated ? 100 : 0 },
          display: "flex",
          alignItems: "center"
        }}>
        <Pan
          className="pan"
          style={{
            transform: isRotated ? "rotate(-90deg)" : "none",
            fontSize: 115,
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: -1
          }}
        />
        <Box
          className="info_block"
          sx={{
            mb: 2.5,
            position: "relative",
            width: "100px",
            ml: 7,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            transition: "all 600ms ease-in-out"
          }}>
          <DefaultLink
            data-cy={`recipe_link recipe_${recipe.pk}_link`}
            sx={{ pointerEvents: isRotated ? "auto" : "none" }}
            className="recipe_title"
            underline={"hover"}
            href={`/recipe/${recipe.pk}`}>
            <Typography
              sx={{
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 4
              }}
              fontWeight={600}
              fontSize={16}>
              {title}
            </Typography>
          </DefaultLink>
          <Box
            className="hidden_block"
            sx={{
              opacity: 0,
              position: "absolute",
              bottom: -60,
              left: 0,
              transition: "all 600ms ease-in-out"
            }}>
            <Typography
              maxWidth={120}
              fontWeight={400}
              fontSize={14}
              color="#626262"
              noWrap>
              {first_name} {last_name}
            </Typography>
            <Box sx={{ display: "flex", "& path": { fill: "#344054" } }}>
              {!isNonProductionRecipe(recipe.sale_status) ? (
                <IconButton
                  onClick={handleSavedClick}
                  sx={{
                    "& path": {
                      fill: favoriteColor()
                    }
                  }}>
                  <Saved />
                </IconButton>
              ) : (
                <IconButton
                  disabled={isLikedLoading}
                  onClick={handleLikeClick}
                  sx={{
                    p: 0,
                    "& path": {
                      fill: isLiked
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
                      fillOpacity: 1
                    }
                  }}>
                  {isLiked ? <LikedIcon /> : <LikeIcon />}
                </IconButton>
              )}
              <Divider orientation="vertical" flexItem variant="middle" />
              {!isNonProductionRecipe(recipe.sale_status) && (
                <>
                  <IconButton
                    disabled={isInCart({ id: recipe.pk })}
                    aria-label="Add_to_Cart"
                    data-cy="add_recipe_to_cart"
                    sx={{ "&:disabled": { "& path": { fill: "lightgray" } } }}
                    onClick={handleShoppingBagClick}>
                    <ShoppingBag />
                  </IconButton>
                  <Divider
                    color="primary"
                    orientation="vertical"
                    flexItem
                    variant="middle"
                  />
                </>
              )}
              <DefaultLink href={`/play/${recipe.pk}`}>
                <IconButton>
                  <Headset />
                </IconButton>
              </DefaultLink>
            </Box>
          </Box>
        </Box>
        <Avatar
          className="avatar"
          sx={{
            position: "absolute",
            width: 55,
            height: 55,
            top: 23,
            left: 20,
            opacity: 0,
            transition: "all 600ms ease-in-out",
            zIndex: -2
          }}>
          {avatar_thumbnail_55 && (
            <Image
              src={avatar_thumbnail_55.toString()}
              layout={"fill"}
              objectFit={"cover"}
              alt="avatar"
            />
          )}
        </Avatar>
      </Box>
    );
  },
  (prevProps, newProps) => {
    return prevProps.recipe.pk === newProps.recipe.pk;
  }
);

RecipePan.displayName = "RecipePan";

export default RecipePan;
